import { useRef, useEffect, useState, useMemo } from 'react';
import { useTheme } from '@simplywallst/ui-core';
import {
  TabSelectionWrapper,
  TabRow,
  TabWrapper,
  TabButton,
  TabOverflowEdges,
  TabOverflowShadow,
} from './styled';
import type { useIndustryCompanies } from '../../../../hooks/useIndustryCompanies';
import useDimensions from 'react-cool-dimensions';
import useWindowSize from '@/hooks/useWindowSize';
import { debounce } from 'lodash';
import { toTruncated } from '@/utilities/formatting';
import { useIndustryContext } from '../../../IndustryContextProvider';

interface Props {
  industryCompanies: ReturnType<
    typeof useIndustryCompanies
  >['industryCompanies'];
  preferredMultiple?: string;
}

const TabSelection = ({ industryCompanies, preferredMultiple }: Props) => {
  const { index, multiple, setIndex } = useIndustryContext();
  const { companies, max } = industryCompanies;
  const { observe, width: chartWidth } = useDimensions();
  const { width: screenWidth } = useWindowSize();
  const tabRowRef = useRef<HTMLDivElement>(null);
  const leftOverflowRef = useRef<HTMLDivElement>(null);
  const rightOverflowRef = useRef<HTMLDivElement>(null);
  const [showLeftShadow, setShowLeftShadow] = useState<boolean>(false);
  const [showRightShadow, setShowRightShadow] = useState<boolean>(false);
  const theme = useTheme();

  //Center selected tab on index change
  useEffect(() => {
    const tabSize =
      screenWidth !== undefined && screenWidth <= theme.smMaxRaw ? 88 : 128;

    const deboucedScrollTo = debounce(() => {
      if (!tabRowRef.current) return;
      tabRowRef.current.scrollTo({
        top: 0,
        left: tabSize * index - (chartWidth - tabSize) / 2,
        behavior: 'smooth',
      });
    }, 200);
    deboucedScrollTo();
  }, [index, chartWidth, screenWidth, theme.smMaxRaw]);

  const resolvedMultiple = multiple || preferredMultiple;
  const tabs = useMemo(
    () =>
      companies.map((c, i) => {
        const label = `${resolvedMultiple?.toUpperCase()} ${toTruncated(
          c.from || 0,
          1
        )}–${toTruncated(c.to || max, 1)}`;
        return (
          <TabWrapper key={c.key}>
            <TabButton
              key={c.key}
              selected={index === i}
              onClick={() => setIndex(i)}
              data-cy-id={`vs-industry-tab-${i}`}
            >
              {i === companies.length - 1
                ? `${resolvedMultiple?.toUpperCase()} ${c.from}+`
                : label}
            </TabButton>
            {i === 0 && <TabOverflowEdges side="left" ref={leftOverflowRef} />}
            {i === companies.length - 1 && (
              <TabOverflowEdges side="right" ref={rightOverflowRef} />
            )}
          </TabWrapper>
        );
      }),
    [companies, max, index, setIndex, resolvedMultiple]
  );

  useEffect(() => {
    const handleIntersect =
      (setter: React.Dispatch<React.SetStateAction<boolean>>) =>
      (e: IntersectionObserverEntry[]) => {
        const event = e[0];
        if (event && event.isIntersecting === true) {
          setter(false);
        } else {
          setter(true);
        }
      };

    const observerLeft = new IntersectionObserver(
      handleIntersect(setShowLeftShadow),
      {
        root: tabRowRef.current,
      }
    );
    const observerRight = new IntersectionObserver(
      handleIntersect(setShowRightShadow),
      {
        root: tabRowRef.current,
      }
    );

    leftOverflowRef.current && observerLeft.observe(leftOverflowRef.current);
    rightOverflowRef.current && observerRight.observe(rightOverflowRef.current);

    return () => {
      observerLeft.disconnect();
      observerRight.disconnect();
    };
  }, []);

  return (
    <TabSelectionWrapper ref={observe}>
      <TabRow ref={tabRowRef} width={chartWidth}>
        {tabs}
      </TabRow>
      {showLeftShadow && <TabOverflowShadow side="left" />}
      {showRightShadow && <TabOverflowShadow side="right" />}
    </TabSelectionWrapper>
  );
};
export default TabSelection;
